// Module rail — sortable icon grid with right-click context menu.
import { useMemo, useRef, useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  arrayMove,
  rectSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { I } from "../shared/Icon";
import { MODULES, MODULE_SECTIONS, ADMIN_MODULE, type Module } from "./modules";
import { useAuth } from "../shared/stores/auth";
import { useTabs } from "./tabsStore";
import { api } from "../shared/api/client";

type Props = {
  width: number;
  minimized: boolean;
  onResize: (w: number) => void;
  onMinimize: (m: boolean) => void;
};

const EXEC_ROLES = new Set(["SUPER_ADMIN", "C_LEVEL"]);

// Debounced save to server
let _saveTimer: ReturnType<typeof setTimeout> | null = null;
function saveOrder(order: string[]) {
  if (_saveTimer) clearTimeout(_saveTimer);
  _saveTimer = setTimeout(() => {
    api.put("/users/me/preferences", { rail_order: JSON.stringify(order) }).catch(() => {});
  }, 800);
}

type ContextMenu = { x: number; y: number; modId: string } | null;

export function Rail({ width, minimized, onResize, onMinimize }: Props) {
  const nav = useNavigate();
  const loc = useLocation();
  // 설치형(멀티탭) 모드인지 — 여기선 Ctrl/⌘·가운데클릭이 인앱 새 탭을 연다.
  const multiTab = useMemo(() => {
    try {
      if (localStorage.getItem("t-tabs-force") === "1") return true;
      if (/electron/i.test(navigator.userAgent)) return true;
      return !!window.matchMedia?.("(display-mode: standalone)")?.matches
        || (navigator as unknown as { standalone?: boolean }).standalone === true;
    } catch { return false; }
  }, []);
  // 메뉴 클릭: Ctrl/⌘ 또는 가운데 버튼이면 새 탭으로 연다(그 외엔 현재 탭 이동).
  const openMod = (m: Module, e: React.MouseEvent) => {
    const wantNew = e.ctrlKey || e.metaKey || e.button === 1;
    if (wantNew) {
      e.preventDefault();
      if (multiTab) {
        useTabs.getState().openNew({ url: m.path, title: m.label, icon: m.icon, color: m.color });
        nav(m.path);
      } else {
        window.open(m.path, "_blank");   // 브라우저: 새 브라우저 탭
      }
      return;
    }
    nav(m.path);
  };
  const manifest = useAuth((s) => s.manifest);
  const user = useAuth((s) => s.user);
  const dragging = useRef<{ startX: number; startW: number } | null>(null);
  const [order, setOrder] = useState<string[] | null>(null); // null = default order
  const [ctxMenu, setCtxMenu] = useState<ContextMenu>(null);
  const [badges, setBadges] = useState<Record<string, number>>({}); // module id → 미확인 수

  const isExec = useMemo(() => {
    if (!user) return false;
    const roles: string[] = (user as any).roles ?? (user as any).role_codes ?? [];
    return roles.some((r) => EXEC_ROLES.has(r));
  }, [user]);

  const visible = useMemo(() => {
    return MODULES.filter((m) => {
      if (m.execOnly && !isExec) return false;
      return !m.menuKey || manifest?.menus?.[m.menuKey] !== false;
    });
  }, [manifest, isExec]);

  // Load saved order from preferences on mount
  useEffect(() => {
    api.get<{ rail_order?: string | null }>("/users/me/preferences")
      .then((r) => {
        const raw = r.data.rail_order;
        if (raw) {
          try { setOrder(JSON.parse(raw)); } catch {}
        }
      })
      .catch(() => {});
  }, []);

  // Unread/mention badge counts → mapped to module ids (mail·chat→msg·todos·notice).
  useEffect(() => {
    const load = () => api.get<{ mail: number; chat: number; todos: number; notice: number }>("/dashboard/badges")
      .then((r) => setBadges({ mail: r.data.mail, msg: r.data.chat, todos: r.data.todos, notice: r.data.notice }))
      .catch(() => {});
    load();
    const t = setInterval(load, 60000); // 주기 갱신(다른 기기에서 읽은 경우 대비)

    // 폴링만 두면 메일을 읽어도 최대 1분간 숫자가 그대로라 "안 바뀐다"고 느낀다.
    // 각 모듈이 상태를 바꿀 때 쏘는 이벤트를 듣고 즉시 다시 센다.
    const onChange = () => load();
    for (const ev of ["mail:refresh", "chat:refresh", "todos:refresh", "notice:refresh"]) {
      window.addEventListener(ev, onChange);
    }
    // 다른 탭·창에서 읽고 돌아온 경우에도 즉시 맞춘다.
    const onVisible = () => { if (document.visibilityState === "visible") load(); };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onChange);

    return () => {
      clearInterval(t);
      for (const ev of ["mail:refresh", "chat:refresh", "todos:refresh", "notice:refresh"]) {
        window.removeEventListener(ev, onChange);
      }
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onChange);
    };
  }, []);

  // Apply custom order or keep default
  const orderedModules = useMemo(() => {
    if (!order) return visible;
    const map = new Map(visible.map((m) => [m.id, m]));
    const ordered = order.flatMap((id) => (map.has(id) ? [map.get(id)!] : []));
    // Append any new modules not in saved order
    const inOrder = new Set(order);
    visible.forEach((m) => { if (!inOrder.has(m.id)) ordered.push(m); });
    return ordered;
  }, [visible, order]);

  const cols = minimized ? 1 : (width < 100 ? 1 : width < 180 ? 2 : 3);

  // Group ordered modules by section (preserves custom order within each section)
  const groupedModules = useMemo(() => {
    const result: Record<string, Module[]> = {};
    for (const sec of MODULE_SECTIONS) result[sec.id] = [];
    for (const mod of orderedModules) {
      if (result[mod.section]) result[mod.section].push(mod);
    }
    return result;
  }, [orderedModules]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const ids = orderedModules.map((m) => m.id);
    const oldIdx = ids.indexOf(active.id as string);
    const newIdx = ids.indexOf(over.id as string);
    if (oldIdx < 0 || newIdx < 0) return;
    const newOrder = arrayMove(ids, oldIdx, newIdx);
    setOrder(newOrder);
    saveOrder(newOrder);
  }, [orderedModules]);

  const resetOrder = () => {
    setOrder(null);
    api.put("/users/me/preferences", { rail_order: null }).catch(() => {});
    setCtxMenu(null);
  };

  // Close context menu on outside click
  useEffect(() => {
    if (!ctxMenu) return;
    const close = () => setCtxMenu(null);
    window.addEventListener("click", close);
    return () => window.removeEventListener("click", close);
  }, [ctxMenu]);

  const openCtx = (e: React.MouseEvent, modId: string) => {
    e.preventDefault();
    e.stopPropagation();
    setCtxMenu({ x: e.clientX, y: e.clientY, modId });
  };

  const moveInOrder = (modId: string, dir: -1 | 1) => {
    const mod = orderedModules.find((m) => m.id === modId);
    if (!mod) { setCtxMenu(null); return; }
    // Only allow reordering within the same section
    const sectionIds = orderedModules.filter((m) => m.section === mod.section).map((m) => m.id);
    const secIdx = sectionIds.indexOf(modId);
    const nextSecIdx = secIdx + dir;
    if (nextSecIdx < 0 || nextSecIdx >= sectionIds.length) { setCtxMenu(null); return; }
    const ids = orderedModules.map((m) => m.id);
    const oldIdx = ids.indexOf(modId);
    const newIdx = ids.indexOf(sectionIds[nextSecIdx]);
    const newOrder = arrayMove(ids, oldIdx, newIdx);
    setOrder(newOrder);
    saveOrder(newOrder);
    setCtxMenu(null);
  };

  // Resize handle
  useEffect(() => {
    const move = (e: MouseEvent) => {
      if (!dragging.current) return;
      const dx = e.clientX - dragging.current.startX;
      const next = Math.min(240, Math.max(72, dragging.current.startW + dx));
      onResize(next);
    };
    const up = () => { dragging.current = null; document.body.style.cursor = ""; };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    return () => { window.removeEventListener("mousemove", move); window.removeEventListener("mouseup", up); };
  }, [onResize]);

  const startDrag = (e: React.MouseEvent) => {
    e.preventDefault();
    dragging.current = { startX: e.clientX, startW: width };
    document.body.style.cursor = "col-resize";
  };

  return (
    <>
      <div className="t-rail" style={{ width: minimized ? 56 : width }}>
        <div className="t-rail-logo" style={{ justifyContent: minimized ? "center" : undefined }}>
          {minimized ? (
            <button className="t-iconbtn" style={{ width: 32, height: 32 }}
              onClick={() => onMinimize(false)} title="펼치기">
              <I.PanelR size={16}/>
            </button>
          ) : (
            <>
              <span style={{ width: 22, flexShrink: 0 }} />
              <span style={{ flex: 1, textAlign: "center", fontFamily: "'Pretendard Variable', 'Pretendard', sans-serif", fontSize: 17, fontWeight: 700, color: "var(--text-1)", letterSpacing: "0.02em" }}>
                TIM_BOX
              </span>
              <button className="t-iconbtn" style={{ width: 22, height: 22, flexShrink: 0 }}
                onClick={() => onMinimize(true)} title="최소화">
                <I.PanelL size={14}/>
              </button>
            </>
          )}
        </div>

        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <div className="t-rail-scroll">
            {MODULE_SECTIONS.map((sec) => {
              const items = groupedModules[sec.id] ?? [];
              if (!items.length) return null;
              return (
                <div key={sec.id} className="t-rail-section-block">
                  <div className="t-rail-section-label">
                    <div className="bar" style={{ background: sec.color }} />
                    {!minimized && (
                      <div className="lbl" style={{ color: sec.color }}>
                        {sec.label}
                        {sec.id === "exec" && <span style={{ fontSize: 8, marginLeft: 4, opacity: 0.7 }}>임원</span>}
                      </div>
                    )}
                  </div>
                  <SortableContext items={items.map((m) => m.id)} strategy={rectSortingStrategy}>
                    <div
                      className="t-rail-section-items"
                      style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}
                    >
                      {items.map((m) => (
                        <SortableRailItem key={m.id} mod={badges[m.id] ? { ...m, badge: badges[m.id] } : m}
                          active={loc.pathname.startsWith(m.path)}
                          onClick={(e) => openMod(m, e)}
                          minimized={minimized}
                          onContextMenu={(e) => openCtx(e, m.id)}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </div>
              );
            })}
          </div>
        </DndContext>

        <div className="t-rail-footer">
          <button className="solid" onClick={(e) => openMod(ADMIN_MODULE, e)}
            onAuxClick={(e) => { if (e.button === 1) openMod(ADMIN_MODULE, e); }} title="시스템 관리">
            <I.Settings size={14}/>
            {!minimized && <span>시스템</span>}
          </button>
        </div>

        <div className="t-rail-resize-handle" onMouseDown={startDrag} title="드래그로 폭 조정" />
      </div>

      {/* Context menu */}
      {ctxMenu && (
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            position: "fixed", left: ctxMenu.x, top: ctxMenu.y, zIndex: 9999,
            background: "var(--bg-0, white)", border: "1px solid var(--border)",
            borderRadius: 8, boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
            minWidth: 150, padding: 4,
          }}
        >
          {[
            { label: "위로 이동", action: () => moveInOrder(ctxMenu.modId, -1) },
            { label: "아래로 이동", action: () => moveInOrder(ctxMenu.modId, 1) },
            { label: "기본 순서로 복원", action: resetOrder, sep: true },
          ].map((item) => (
            <button key={item.label}
              onClick={item.action}
              style={{
                display: "block", width: "100%", textAlign: "left",
                padding: "6px 12px", fontSize: 13, background: "none", border: "none",
                cursor: "pointer", borderRadius: 5,
                borderTop: item.sep ? "1px solid var(--border)" : undefined,
                marginTop: item.sep ? 4 : 0,
              }}
              onMouseEnter={(e) => { (e.target as HTMLElement).style.background = "var(--panel)"; }}
              onMouseLeave={(e) => { (e.target as HTMLElement).style.background = "none"; }}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
    </>
  );
}

function SortableRailItem({
  mod, active, onClick, minimized, onContextMenu,
}: {
  mod: Module; active: boolean; onClick: (e: React.MouseEvent) => void; minimized: boolean;
  onContextMenu: (e: React.MouseEvent) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: mod.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  const Ic = I[mod.icon];
  const isExecMod = mod.section === "exec";
  return (
    <button
      ref={setNodeRef}
      style={{
        ...style,
        width: minimized ? 40 : "min(54px, 100%)",
        height: minimized ? 40 : 54,
        padding: minimized ? 0 : "2px 0",
        borderLeft: isExecMod && active ? `2px solid ${mod.color}` : undefined,
      }}
      className={`t-rail-item ${active ? "active" : ""}`}
      onClick={onClick}
      onAuxClick={(e) => { if (e.button === 1) onClick(e); }}
      onContextMenu={onContextMenu}
      title={mod.label}
      {...attributes}
      {...listeners}
    >
      <Ic size={minimized ? 18 : 20} style={{ color: active ? mod.color : isExecMod ? "#DC262680" : "currentColor" }}/>
      {!minimized && (
        // 띄어쓰기가 있는 긴 이름(예: '통합 문서 작성')은 잘라내지 않고 두 줄로 보여 준다.
        mod.label.length > 6 && mod.label.includes(" ") ? (
          <span className="rl-label" style={{ fontSize: 9, whiteSpace: "normal", textAlign: "center", lineHeight: 1.05, color: isExecMod && !active ? "#DC262699" : undefined }}>
            {mod.label}
          </span>
        ) : (
          <span className="rl-label" style={{ fontSize: 10, color: isExecMod && !active ? "#DC262699" : undefined }}>
            {mod.label.slice(0, 6)}
          </span>
        )
      )}
      {mod.badge !== undefined && mod.badge > 0 && <span className="badge-pill">{mod.badge > 99 ? "99+" : mod.badge}</span>}
    </button>
  );
}
